import { Theme } from '@mui/material';
import { CSSObject } from 'tss-react';

export type LabeledChipAppearance = 'default' | 'overlay' | 'plain';

const OVERLAY_BLUR_IN_PIXELS = 4;

export const LABELED_CHIP_ICON_INLINE_START_SPACING = 0.5;

export const getLabeledChipBaseStyles = (theme: Theme, hasLeadingIcon: boolean): CSSObject => ({
  '& .MuiChip-label': {
    textAlign: 'center',
    paddingInline: theme.spacing(0.5),
  },
  '& .MuiChip-label:empty': {
    paddingInline: 0,
  },
  '& .MuiChip-icon': {
    marginLeft: hasLeadingIcon ? theme.spacing(LABELED_CHIP_ICON_INLINE_START_SPACING) : 0,
    marginRight: 0,
    fontSize: theme.typography.body2.fontSize,
    fontWeight: theme.typography.body2.fontWeight,
    lineHeight: theme.typography.body2.lineHeight,
  },
  '& .MuiChip-icon > svg': {
    display: 'block',
  },
  lineHeight: 1,
});

const getAppearanceStyles = (theme: Theme, appearance: LabeledChipAppearance): CSSObject => {
  if (appearance === 'overlay') {
    return {
      color: theme.palette.common.white,
      backgroundColor: theme.palette.custom.overlay,
      backdropFilter: `blur(${OVERLAY_BLUR_IN_PIXELS}px)`,
      boxShadow: 'none',
      borderRadius: `${theme.shape.borderRadiusMd}px`,
      '& .MuiChip-icon': { color: 'inherit' },
    };
  }

  if (appearance === 'plain') {
    return {
      height: 'auto',
      color: theme.palette.text.secondary,
      backgroundColor: 'transparent',
      '&:hover': { backgroundColor: 'transparent' },
      backdropFilter: 'none',
      boxShadow: 'none',
      borderRadius: `${theme.shape.borderRadiusFull}px`,
      paddingBlock: theme.spacing(0.5),
      paddingInline: theme.spacing(0.25),
    };
  }

  return { color: theme.palette.text.primary };
};

const keepBackgroundOnHover = (styles: CSSObject): CSSObject =>
  styles.backgroundColor
    ? { ...styles, '&:hover': { backgroundColor: styles.backgroundColor } }
    : styles;

export const getLabeledChipAppearanceStyles = (
  theme: Theme,
  appearance: LabeledChipAppearance,
): CSSObject => keepBackgroundOnHover(getAppearanceStyles(theme, appearance));
